import { makeAutoObservable, observable, runInAction } from "mobx";
import ChartService, { ChartQuery } from "../services/chartService";
import {
  DeviceChartData,
  DeviceChartType,
} from "../models/chartData";

export type SeriesVisibility = Record<string, boolean>;
export type DeviceSeriesVisibility = Record<string, SeriesVisibility>;

export type DetailLevel = "month" | "week" | "day";

interface Range {
  from: Date;
  to: Date;
}

export class ModuleChartStore {
  moduleId: number | null = null;
  deviceType: DeviceChartType | null = null;
  data: DeviceChartData | null = null;
  loading = false;
  error: string | null = null;

  // === Навигация «месяц + уровень» (МДБН-стиль) ===
  selectedMonth: Date = new Date();
  detailLevel: DetailLevel = "month";
  loadedFrom: string | null = null;
  loadedTo: string | null = null;
  cursorPoint: number | null = null;
  wheelValue: number = 50; // не observable — не должен триггерить рендер
  zoomResetVersion = 0;
  zoomStart = 0;
  zoomEnd = 100;

  // === Видимость серий по chart.id ===
  activeSeries: DeviceSeriesVisibility = {};

  constructor() {
    const savedMonth =
      typeof window !== "undefined"
        ? localStorage.getItem("selectedMonth")
        : null;
    if (savedMonth) {
      this.selectedMonth = new Date(savedMonth);
    }

    makeAutoObservable(this, {
      wheelValue: observable.ref,
    });
  }

  // === API ===

  getChart(id: string) {
    return this.data?.charts.find((chart) => chart.id === id);
  }

  get limits() {
    return this.data?.limits || [];
  }

  get minDate(): number | undefined {
    return this.loadedFrom ? new Date(this.loadedFrom).getTime() : undefined;
  }

  get maxDate(): number | undefined {
    return this.loadedTo ? new Date(this.loadedTo).getTime() : undefined;
  }

  load(moduleId: number, deviceType: DeviceChartType) {
    this.moduleId = moduleId;
    this.deviceType = deviceType;

    const range = this.getRangeByLevel(this.detailLevel, this.selectedMonth);
    const query: ChartQuery = {
      resolution:
        this.detailLevel === "month"
          ? "month"
          : this.detailLevel === "week"
            ? "week"
            : "day",
      from: range.from.toISOString(),
      to: range.to.toISOString(),
    };

    return this.fetchData(query);
  }

  private async fetchData(query: ChartQuery) {
    if (!this.moduleId || !this.deviceType) return;

    this.loading = true;
    this.error = null;

    try {
      const data = await ChartService.fetchChartData(
        this.moduleId,
        this.deviceType,
        query,
      );

      runInAction(() => {
        this.data = data;
        this.loadedFrom = query.from;
        this.loadedTo = query.to;
        this.zoomStart = 0;
        this.zoomEnd = 100;
        this.wheelValue = 50;
        this.initActiveSeries(data);
      });
    } catch (err: any) {
      runInAction(() => {
        this.error =
          err?.response?.data?.error || err?.message || "Ошибка загрузки данных";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  refresh() {
    if (!this.moduleId || !this.deviceType) return;
    return this.load(this.moduleId, this.deviceType);
  }

  // === Серии ===

  initActiveSeries(data: DeviceChartData) {    const prev = this.activeSeries;
    const next: DeviceSeriesVisibility = {};

    data.charts.forEach((chart) => {
      next[chart.id] = {};
      chart.series.forEach((series) => {
        next[chart.id][series.name] = prev?.[chart.id]?.[series.name] ?? true;
      });
    });

    this.activeSeries = next;
  }

  toggleSeries(chartId: string, seriesName: string, selected?: boolean) {
    const current = this.activeSeries[chartId]?.[seriesName] ?? true;
    this.activeSeries = {
      ...this.activeSeries,
      [chartId]: {
        ...(this.activeSeries[chartId] || {}),
        [seriesName]: selected !== undefined ? selected : !current,
      },
    };
  }

  isSeriesVisible(chartId: string, seriesName: string): boolean {
    return this.activeSeries[chartId]?.[seriesName] ?? true;
  }

  // === Навигация ===

  setSelectedMonth(date: Date) {
    this.selectedMonth = date;
    if (this.moduleId && this.deviceType) {
      void this.load(this.moduleId, this.deviceType);
    }
  }

  setDetailLevel(level: DetailLevel) {
    if (this.detailLevel === level) return;
    this.detailLevel = level;
    this.zoomResetVersion++;
    if (this.moduleId && this.deviceType) {
      void this.load(this.moduleId, this.deviceType);
    }
  }

  shiftPeriod(direction: "prev" | "next") {
    if (!this.moduleId) return;

    if (direction === "next") {
      const now = new Date();
      const to = this.loadedTo ? new Date(this.loadedTo) : new Date();
      if (
        to.getFullYear() > now.getFullYear() ||
        (to.getFullYear() === now.getFullYear() &&
          to.getMonth() > now.getMonth())
      ) {
        return;
      }
    }

    const centerTime = this.loadedFrom && this.loadedTo
      ? (new Date(this.loadedFrom).getTime() + new Date(this.loadedTo).getTime()) / 2
      : this.selectedMonth.getTime();
    const centerDate = new Date(centerTime);
    const shiftedDate = this.shiftDateByLevel(centerDate, direction);

    this.selectedMonth = shiftedDate;
    void this.load(this.moduleId, this.deviceType);
  }

  shiftDateByLevel = (date: Date, direction: "prev" | "next"): Date => {
    const d = new Date(date);
    if (this.detailLevel === "day") {
      d.setDate(d.getDate() + (direction === "next" ? 1 : -1));
    } else if (this.detailLevel === "week") {
      d.setDate(d.getDate() + (direction === "next" ? 7 : -7));
    } else if (this.detailLevel === "month") {
      d.setMonth(d.getMonth() + (direction === "next" ? 1 : -1));
    }
    return d;
  };

  getRangeByLevel(level: DetailLevel, baseDate: Date): Range {
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const date = baseDate.getDate();

    switch (level) {
      case "month":
        return {
          from: new Date(year, month, 1),
          to: new Date(year, month + 1, 0, 23, 59, 59, 999),
        };
      case "week": {
        const startOfWeek = new Date(baseDate);
        startOfWeek.setDate(date - (baseDate.getDay() || 7) + 1);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return { from: startOfWeek, to: endOfWeek };
      }
      case "day":
        return {
          from: new Date(year, month, date),
          to: new Date(year, month, date, 23, 59, 59, 999),
        };
    }
  }

  getRangeByCursor(level: DetailLevel, cursorTime: number): Range {
    const deltaMs = {
      month: 15 * 24 * 60 * 60 * 1000,
      week: 3 * 24 * 60 * 60 * 1000,
      day: 12 * 60 * 60 * 1000,
    };
    const delta = deltaMs[level];
    return {
      from: new Date(cursorTime - delta),
      to: new Date(cursorTime + delta),
    };
  }

  handleWheelDelta(delta: number) {
    let newValue = this.wheelValue + delta;
    newValue = Math.min(Math.max(newValue, 0), 100);

    const currentLevel = this.detailLevel;
    let targetLevel: DetailLevel = currentLevel;

    if (currentLevel === "month") {
      if (newValue <= 30) targetLevel = "week";
    } else if (currentLevel === "week") {
      if (newValue >= 80) targetLevel = "month";
      else if (newValue <= 20) targetLevel = "day";
    } else if (currentLevel === "day") {
      if (newValue >= 70) targetLevel = "week";
    }

    if (targetLevel !== currentLevel) {
      this.detailLevel = targetLevel;
      this.zoomResetVersion++;

      const cursorTime =
        this.cursorPoint ??
        (this.loadedFrom && this.loadedTo
          ? (new Date(this.loadedFrom).getTime() +
              new Date(this.loadedTo).getTime()) /
            2
          : Date.now());

      const range = this.getRangeByCursor(targetLevel, cursorTime);
      const query: ChartQuery = {
        resolution:
          targetLevel === "month"
            ? "month"
            : targetLevel === "week"
              ? "week"
              : "day",
        from: range.from.toISOString(),
        to: range.to.toISOString(),
      };
      void this.fetchData(query);
    } else {
      this.wheelValue = newValue;
    }
  }

  handleDataZoom(params: any) {
    if (!this.data) return;
    if (!this.loadedFrom || !this.loadedTo) return;

    const startPercent = params.batch?.[0]?.start ?? 0;
    const endPercent = params.batch?.[0]?.end ?? 100;
    if (startPercent === 0 && endPercent === 100) return;

    this.zoomStart = startPercent;
    this.zoomEnd = endPercent;
  }

  setCursorPoint(value: number | null) {
    this.cursorPoint = value;
  }

  // === Сброс ===

  reset() {
    this.data = null;
    this.loading = false;
    this.error = null;
    this.loadedFrom = null;
    this.loadedTo = null;
    this.activeSeries = {};
  }
}

export const moduleChartStore = new ModuleChartStore();
